import {Box} from '@mui/material';
import {DateTime, Duration} from 'luxon';
import {useListHomesForConversationsQuery, colors} from '@ark/react';
import {useUserProfile} from '../../../contexts/UserProfile';
import {useLogEvent} from '../../../hooks/useLogEvent/useLogEvent';
import {CommunityFilter} from '../../inputs/CommunityFilter';
import {SearchSelect, SearchSelectValue} from '../../inputs/SearchSelect';
import {Bedtime, CheckCircle, Circle} from '@mui/icons-material';

export type FilterName =
  | 'communities'
  | 'home'
  | 'date'
  | 'status'
  | 'tenantStatus'
  | 'assignedTo'
  | 'sortBy'
  | 'unanswered';

export type FilterValues = {
  home: SearchSelectValue;
  date: SearchSelectValue;
  status: SearchSelectValue;
  tenantStatus: SearchSelectValue;
  orderBy: SearchSelectValue;
  assignedTo: SearchSelectValue;
  unanswered: SearchSelectValue;
};

const sortOptions: {value: string; label: string}[] = [
  {value: 'last_event_time', label: 'Oldest'},
  {value: '-last_event_time', label: 'Newest'},
  {value: 'latest_updated_date', label: 'Waiting Longest'},
  {value: 'urgencies__urgency', label: 'Priority'},
];

const dateFilterOptions: {value: string; label: string}[] = [
  {value: DateTime.now().toISODate() as string, label: 'Today'},
  {
    value: DateTime.now()
      .minus(Duration.fromObject({days: 1}).toMillis())
      .toISODate() as string,
    label: 'Yesterday',
  },
  {
    value: DateTime.now()
      .minus(Duration.fromObject({days: 7}).toMillis())
      .toISODate() as string,
    label: 'Last 7 days',
  },
  {
    value: DateTime.now()
      .minus(Duration.fromObject({days: 30}).toMillis())
      .toISODate() as string,
    label: 'Last 30 days',
  },
  {
    value: DateTime.now()
      .minus(Duration.fromObject({days: 90}).toMillis())
      .toISODate() as string,
    label: 'Last 90 days',
  },
];

const assignedToFilterOptions = (userId: string) => {
  return [
    {value: 'All', label: 'All'},
    {value: userId, label: 'Me'},
    {value: 'Unassigned', label: 'Unassigned'},
  ];
};

const unansweredFilterOptions = [
  {value: 'unanswered', label: 'Unanswered'},
  {value: 'answered', label: 'Answered'},
  {value: 'all', label: 'All'},
];

export const openFilter = {
  value: 'OP',
  label: 'Open',
  icon: <Circle color="error" sx={{fontSize: 14, color: colors.treePoppy}} />,
};
export const tenantOpenFilter = {value: 'OP,SN', label: 'Open'};
const snoozeFilter = {
  value: 'SN',
  label: 'Snoozed',
  icon: (
    <Bedtime color="error" sx={{fontSize: 14, color: colors.fadedPurple}} />
  ),
};
const closedFilter = {
  value: 'CL',
  label: 'Closed',
  icon: (
    <CheckCircle color="error" sx={{fontSize: 14, color: colors['gray.600']}} />
  ),
};

const statusFilterOptions = [openFilter, snoozeFilter, closedFilter];
const tenantStatusFilterOptions = [tenantOpenFilter, closedFilter];

type Props = {
  enabledFilters: FilterName[];
  filterValues: FilterValues;
  setFilterValues: (filters: FilterValues) => void;
};

export const ConversationsFilters = ({
  enabledFilters,
  filterValues,
  setFilterValues,
}: Props) => {
  const logEvent = useLogEvent();

  const setSortBy = (value: SearchSelectValue) => {
    setFilterValues({...filterValues, orderBy: value});
    logEvent({
      name: 'Conversation Filter Changed',
      data: {filterType: 'sortBy', newValue: value[0]?.value},
    });
  };
  const setDateFilter = (value: SearchSelectValue) => {
    setFilterValues({...filterValues, date: value});
    logEvent({
      name: 'Conversation Filter Changed',
      data: {filterType: 'date', newValue: value[0]?.value},
    });
  };
  const setStatusFilter = (value: SearchSelectValue) => {
    setFilterValues({...filterValues, status: value});
    logEvent({
      name: 'Conversation Filter Changed',
      data: {filterType: 'status', newValue: value[0]?.value},
    });
  };
  const setTenantStatusFilter = (value: SearchSelectValue) => {
    setFilterValues({...filterValues, tenantStatus: value});
    logEvent({
      name: 'Conversation Filter Changed',
      data: {filterType: 'tenantStatus', newValue: value[0]?.value},
    });
  };
  const setHomeFilter = (value: SearchSelectValue) => {
    setFilterValues({...filterValues, home: value});
    logEvent({
      name: 'Conversation Filter Changed',
      data: {filterType: 'home', newValue: value[0]?.value},
    });
  };
  const setAssignedToFilter = (value: SearchSelectValue) => {
    setFilterValues({...filterValues, assignedTo: value});
    logEvent({
      name: 'Conversation Filter Changed',
      data: {filterType: 'assignedTo', newValue: value[0]?.value},
    });
  };
  const setUnansweredFilter = (value: SearchSelectValue) => {
    setFilterValues({...filterValues, unanswered: value});
    logEvent({
      name: 'Conversation Filter Changed',
      data: {filterType: 'unanswered', newValue: value[0]?.value},
    });
  };
  const {id} = useUserProfile();

  const {data} = useListHomesForConversationsQuery({
    skip: !enabledFilters.includes('home'),
  });

  const homesOptions =
    data?.listHomes?.map((home: {id: string; name: string}) => ({
      value: home.id,
      label: home.name,
    })) || [];

  const autoCompleteWidth = enabledFilters.length > 3 ? 250 : 175;

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
        mb: 1,
        flexDirection: {xs: 'column', md: 'row'},
      }}
    >
      {enabledFilters.includes('communities') && (
        <Box sx={{flexGrow: 1}}>
          <CommunityFilter fullWidth />
        </Box>
      )}
      {enabledFilters.includes('home') && (
        <Box sx={{flexGrow: 1}}>
          <SearchSelect
            filterSelectedOptions
            options={homesOptions}
            value={filterValues.home}
            setValue={setHomeFilter}
            data-attr="conversations-home-filter-select"
            label="Home"
            fullWidth
            minWidth={autoCompleteWidth}
          />
        </Box>
      )}
      {enabledFilters.includes('date') && (
        <Box sx={{flexGrow: 1}}>
          <SearchSelect
            filterSelectedOptions
            options={dateFilterOptions}
            value={filterValues.date}
            setValue={setDateFilter}
            data-attr="conversations-date-filter-select"
            label="Date"
            fullWidth
            minWidth={autoCompleteWidth}
          />
        </Box>
      )}
      {enabledFilters.includes('unanswered') && (
        <Box sx={{flexGrow: 1}}>
          <SearchSelect
            options={unansweredFilterOptions}
            value={filterValues.unanswered}
            setValue={setUnansweredFilter}
            data-attr="conversations-unanswered-filter-select"
            label="Unanswered"
            fullWidth
            minWidth={autoCompleteWidth}
          />
        </Box>
      )}
      {enabledFilters.includes('status') && (
        <Box sx={{flexGrow: 1}}>
          <SearchSelect
            filterSelectedOptions
            options={statusFilterOptions}
            value={filterValues.status}
            setValue={setStatusFilter}
            data-attr="conversations-status-filter-select"
            label="Status"
            multiple
            fullWidth
            minWidth={autoCompleteWidth}
          />
        </Box>
      )}
      {enabledFilters.includes('tenantStatus') && (
        <Box sx={{flexGrow: 1}}>
          <SearchSelect
            filterSelectedOptions
            options={tenantStatusFilterOptions}
            value={filterValues.tenantStatus}
            setValue={setTenantStatusFilter}
            data-attr="conversations-tenant-status-filter-select"
            label="Status"
            multiple
            fullWidth
            minWidth={autoCompleteWidth}
          />
        </Box>
      )}
      {enabledFilters.includes('assignedTo') && id && (
        <Box sx={{flexGrow: 1}}>
          <SearchSelect
            options={assignedToFilterOptions(id)}
            value={filterValues.assignedTo}
            setValue={setAssignedToFilter}
            data-attr="conversations-assigned-to-filter-select"
            label="Assigned to"
            fullWidth
            minWidth={autoCompleteWidth}
          />
        </Box>
      )}
      <Box sx={{flexGrow: 1}}>
        <SearchSelect
          options={sortOptions}
          value={filterValues.orderBy}
          setValue={setSortBy}
          data-attr="conversations-sort-by-filter-select"
          label="Sort by"
          fullWidth
          minWidth={autoCompleteWidth}
        />
      </Box>
    </Box>
  );
};
